import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from 'react-bootstrap';

import SetupStep1 from './SetupStep1';
import SetupStep2 from './SetupStep2';
import SetupStep3 from './SetupStep3';
import SetupStep4 from './SetupStep4';
import SetupStep5 from './SetupStep5';

import { 
    ASSESSMENT_PERIOD, 
    SETUP_MAPPING_VALIDATION_ERROR_1,
    SETUP_MAPPING_VALIDATION_ERROR_2
} from '../../../fastConstants';

const STEPS = ["One", "Two", "Three", "Four"]
const THEMES = {
    1: "Thinking in Action",
    2: "Practicing Community",
    3: "Professional Skills"
}

const FAST_URL = process.env.REACT_APP_DS_API_URL
const MODE = "create"
const LOG_URL = false

export default function SetupCreateForm(props) {
    const [currentStep, setCurrentStep] = useState(1)
    const currentUser = props.username
    const [programId, setProgramId] = useState()
    const [activePrograms, setActivePrograms] = useState({})
    const [submittedPrograms, setSubmittedPrograms] = useState({})
    const [submittedUsers, setSubmittedUsers] = useState({})
    const [submittedDates, setSubmittedDates] = useState({})
    const [learningOutcomeMappings, setLearningOutcomeMappings] = useState({})
    const [errorFound, setErrorFound] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [searchParams] = useSearchParams()
    const departmentId = searchParams.get('d')
    const [departments, setDepartments] = useState(null)
    const [department, setDepartment] = useState()
    const [programLearningOutcomes, setProgramLearningOutcomes] = useState({})
    
    useEffect(() => {
        let url = FAST_URL + "/department"
        if (LOG_URL) console.log(url)
        fetch(url)
        .then(response => {
            return response.json()
        })
        .then(data => {
            let dept = data.find(dept => dept.departmentId === parseInt(searchParams.get('d')))
            setDepartments(data)
            setDepartment(dept)
        })
        .catch(err => {
            console.log(err)
        })
    }, [searchParams])

    useMemo(() => {
        if (department) {
            let url = FAST_URL + "/learning-outcome/active/" + department.departmentId
            if (LOG_URL) console.log(url)
            fetch(url)
            .then(response => {
                return response.json()
            })
            .then(data => {
                let currentSlos = {...programLearningOutcomes}
                currentSlos[department.departmentId] = data
                setProgramLearningOutcomes({...currentSlos})
            })
            .catch(err => {
                console.log(err)
            })
        }
    }, [department, programLearningOutcomes])

    const getProgramLearningOutcomes = (deptId, progId) => {
        let result = programLearningOutcomes[deptId].learningOutcomes
        return result[progId]
    }

    const handleNext = () => {
        // Clear the error message first
        setErrorFound(false)
        setErrorMessage('')

        let canContinue = true;
        switch(currentStep) {
            case 1:
                canContinue = validateStepOne()
                break
            case 2:
                canContinue = validateStepTwo()
                break
            case 3:
                canContinue = validateStepThree()
                break
            case 4:
                canContinue = submitSetup()
                break
            default:
                console.log('Unknown step - ' + currentStep)
        }
        if (canContinue) {
            setCurrentStep(prevStep => prevStep + 1);
        }
    }
    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prevStep => prevStep - 1);
        }
    }

    //
    // There needs to be at least one program selected in order to proceed to step 2.
    //
    const validateStepOne = () => {
        let valid = false
        for (const value of Object.values(activePrograms)) {
            if (value === 'yes') {
                valid = true
                break
            }
        }
        return valid
    }
    const createKey = (programId, sloId, themeId) => {
        return 'program-' + programId + '_slo-' + sloId + '_theme-' + themeId
    }

    const verifyLearningOutcomesAreMapped = () => {
        const programIds = []
        for (const [key,value] of Object.entries(activePrograms)) {
            let k = parseInt(key)
            if (!programIds.includes(k) && value === 'yes') {
                programIds.push(k)
            }
        }
        
        var allThemesMapped = true

        for (let i = 0; i < programIds.length; i++) {
            let departmentSlos = programLearningOutcomes[department.departmentId].learningOutcomes
            
            // verify that each learning outcome is mapped to at least one theme
            departmentSlos[programIds[i]].forEach((slo) => {
                let theme1 = 'program-' + programIds[i] + '_slo-' + slo.sloId + '_theme-1'
                let theme2 = 'program-' + programIds[i] + '_slo-' + slo.sloId + '_theme-2'
                let theme3 = 'program-' + programIds[i] + '_slo-' + slo.sloId + '_theme-3'

                var sloMappedToTheme1 = false
                var sloMappedToTheme2 = false
                var sloMappedToTheme3 = false

                if (theme1 in learningOutcomeMappings && learningOutcomeMappings[theme1] === 'yes') {
                    sloMappedToTheme1 = true
                }
                
                if (theme2 in learningOutcomeMappings && learningOutcomeMappings[theme2] === 'yes') {
                    sloMappedToTheme2 = true
                } 
                
                if (theme3 in learningOutcomeMappings && learningOutcomeMappings[theme3] === 'yes') {
                    sloMappedToTheme3 = true
                }

                // If we find at least one learning outcome NOT mapped to a theme, 
                // the whole verification fails.
                if (sloMappedToTheme1 === false &&  sloMappedToTheme2 === false && sloMappedToTheme3 === false) {
                    allThemesMapped = false
                }
            })
        }
        return allThemesMapped
    }

    const validateMapping = () => {
        const programIds = []
        for (const [key,value] of Object.entries(activePrograms)) {
            let k = parseInt(key)
            if (!programIds.includes(k) && value === 'yes') {
                programIds.push(k)
            }
        }
        
        // Check that at least one "yes" is found in each row per program
        for (let pid = 0; pid < programIds.length; pid++) {
            let obj = getProgramLearningOutcomes(department.departmentId, programIds[pid])
            let programId = programIds[pid]

            for (const value of Object.values(obj)) {
                let sloArray = []

                let sloKey1 = createKey(programId, value.sloId, 1) 
                let sloKey2 = createKey(programId, value.sloId, 2) 
                let sloKey3 = createKey(programId, value.sloId, 3)

                sloArray.push(learningOutcomeMappings[sloKey1])
                sloArray.push(learningOutcomeMappings[sloKey2])
                sloArray.push(learningOutcomeMappings[sloKey3])

                if (!sloArray.includes('yes')) {
                    return false
                }
                
                sloArray = []
            }
        }
        
        // Check that at least one "yes" is found in each col per program
        for (let pid = 0; pid < programIds.length; pid++) {
            let obj = getProgramLearningOutcomes(department.departmentId, programIds[pid])
            let programId = programIds[pid]
            let sloMap = {}
            sloMap['theme-1'] = []
            sloMap['theme-2'] = []
            sloMap['theme-3'] = []

            // sloMap['theme-1'] = ['no', 'yes', 'no'] -- ok
            // sloMap['theme-2'] = ['no', 'no', 'no'] -- err

            for (const key of Object.keys(sloMap)) {
                for (const value of Object.values(obj)) {
                    let sloKey = 'program-' + (programId) + '_slo-' + (value.sloId) + '_' + key
                    sloMap[key].push(learningOutcomeMappings[sloKey])
                }
                if (!sloMap[key].includes('yes')) {
                    return false
                }
            }
        }

        return true
    }

    //
    // Validation of Step 2 (mapping learning outcomes to themes)
    //   1. Each Learning Outcome should be mapped to at least one Theme
    //   2. Each Theme should be mapped to at least one Learning Outcome
    //
    const validateStepTwo = () => {
        if (!verifyLearningOutcomesAreMapped()) {
            setErrorFound(true)
            setErrorMessage(SETUP_MAPPING_VALIDATION_ERROR_1)
            return false
        } else if (!validateMapping()) {
            setErrorFound(true)
            setErrorMessage(SETUP_MAPPING_VALIDATION_ERROR_2)
        } else {
            setErrorFound(false)
            setErrorMessage('')
            return true
        }
    }
    const validateStepThree = () => {
        return true
    }
    const onThemeChanged = (event) => {
        let progId = event.target.name
        let newPrograms = {...activePrograms}
        if (event.target.value === "yes") {
            newPrograms[progId] = event.target.value
        } else {
            if (newPrograms.hasOwnProperty(progId)) {
                let newSubmittedPrograms = {...submittedPrograms}
                let newSubmittedUsers = {...submittedUsers}
                let newSubmittedDates = {...submittedDates}

                delete newSubmittedPrograms[progId]
                delete newSubmittedUsers[progId]
                delete newSubmittedDates[progId]
                delete newPrograms[progId]

                setSubmittedPrograms({...newSubmittedPrograms})
                setSubmittedUsers({...newSubmittedUsers})
                setSubmittedDates({...newSubmittedDates})

                let newLearningOutcomeMappings = {...learningOutcomeMappings}
                for (const key of Object.keys(newLearningOutcomeMappings)) {
                    let tmpKey = 'program-' + progId
                    if (key.includes(tmpKey)) {
                        delete newLearningOutcomeMappings[key]
                    }
                }
                setLearningOutcomeMappings({...newLearningOutcomeMappings})
            }
        }

        setActivePrograms({...newPrograms})
    }
    const onSetupSubmitChanged = (event) => {
        let newSubmittedPrograms = {...submittedPrograms}
        let newSubmittedUsers = {...submittedUsers}
        let newSubmittedDates = {...submittedDates}
        
        if (event.target.value === "yes") {
            const dt = new Date()
            newSubmittedPrograms[event.target.name] = event.target.value
            newSubmittedUsers[event.target.name] = currentUser
            newSubmittedDates[event.target.name] = dt.toISOString()
        } else if (event.target.value === "no") {
            newSubmittedPrograms[event.target.name] = event.target.value
            delete newSubmittedUsers[event.target.name]
        } else {
            if (newSubmittedPrograms.hasOwnProperty(event.target.name)) {
                delete newSubmittedPrograms[event.target.name]
                delete newSubmittedUsers[event.target.name]
            }
        }

        setSubmittedPrograms({...newSubmittedPrograms})
        setSubmittedUsers({...newSubmittedUsers})
        setSubmittedDates({...newSubmittedDates})
    }
    const onLearningOutcomeChanged = (event) => {
        if (event.target.value === '') {
            const newLearningOutcomeMappings = {...learningOutcomeMappings}
            delete newLearningOutcomeMappings[event.target.name]
            setLearningOutcomeMappings({...newLearningOutcomeMappings})
        } else {
            const newLearningOutcomeMappings = {...learningOutcomeMappings, [event.target.name] : event.target.value}
            setLearningOutcomeMappings({...newLearningOutcomeMappings})
        }
    }
    const newAuditInfo = () => {
        const dt = new Date()
        return {
            createdBy: currentUser,
            dateCreated: dt.toISOString(),
            modifiedBy: currentUser,
            dateModified: dt.toISOString()
        }
    }
    const transformMappings = () => {
        // Program ID => Map of Mappings
        let programMappings = {}
        
        for (const [key,value] of Object.entries(learningOutcomeMappings)) {
            // key = 'program-X_slo-Y_theme-Z
            let tokens = key.split('_')
            let programId = parseInt(tokens[0].split('-')[1])
            let learningOutcomeId = tokens[1].split('-')[1]
            
            if (!(programId in programMappings)) {
                programMappings[programId] = {}
            }

            let mappings = programMappings[programId]
            let newKey = tokens[0] + '_' + tokens[1]

            if (!(newKey in mappings)) {
                let mapping = {
                    department: {
                        departmentId: department.departmentId,
                    },
                    program: programId,
                    reviewYear: ASSESSMENT_PERIOD,
                    learningOutcome: parseInt(learningOutcomeId),
                    auditInfo: newAuditInfo(),
                }
    
                if (programId in submittedPrograms) {
                    mapping['submittedBy'] = submittedUsers[programId]
                    mapping['dateSubmitted'] = submittedDates[programId]
                }

                mappings[newKey] = mapping
            }
            
            let mapping = mappings[newKey]

            if (key.endsWith('theme-1')) {
                mapping.firstTheme = THEMES[1]
                mapping.firstThemeAnswer= value
            } else if (key.endsWith('theme-2')) {
                mapping.secondTheme = THEMES[2]
                mapping.secondThemeAnswer= value
            } else if (key.endsWith('theme-3')) {
                mapping.thirdTheme = THEMES[3]
                mapping.thirdThemeAnswer= value
            }
        }
        let newMappings = {}
        for (const key of Object.keys(programMappings)) {
            if (!(key in newMappings)) {
                newMappings[key] = []
            }
            for (const value of Object.values(programMappings[key])) {
                newMappings[key].push(value)
            }
        }

        return newMappings
    }
    const submitSetup = () => {
        let url = FAST_URL + "/setup";
        let departmentSetup = {
            department: {
                departmentId: department.departmentId,
            },
            reviewYear: ASSESSMENT_PERIOD,
            mappings: transformMappings(),
            auditInfo: newAuditInfo(),
        }
        console.log(departmentSetup)
        fetch(url,
        {
            body: JSON.stringify(departmentSetup),
            method: "post",
            headers: { 'Content-Type': 'application/json' },
        })
        .then(res => console.log(res))
        .catch(reason => console.log(reason));
        return true;
    }

    let step;
    if (currentStep === 1) {
        step = <SetupStep1 
                    mode={MODE}
                    departmentId={departmentId} 
                    departments={departments}
                    onThemeChanged={onThemeChanged}
                    activePrograms={activePrograms}
                    programAssessmentsCreated={{}}
                    departmentLearningOutcomes={programLearningOutcomes}
                    />
    } else if (currentStep === 2) {
        step = <SetupStep2
                    mode={MODE}
                    departmentId={departmentId} 
                    departments={departments}
                    programId={programId}
                    onProgramSelect={e => setProgramId(e.target.value)}
                    programLearningOutcomes={programLearningOutcomes[departmentId].learningOutcomes}
                    activePrograms={activePrograms}
                    onLearningOutcomeChanged={onLearningOutcomeChanged}
                    learningOutcomeMappings={learningOutcomeMappings}
                    />
    } else if (currentStep === 3) {
        step = <SetupStep3
                    mode={MODE}
                    currentUser={currentUser}
                    departmentId={departmentId} 
                    departments={departments}
                    programId={programId}
                    activePrograms={activePrograms}
                    programLearningOutcomes={programLearningOutcomes[departmentId].learningOutcomes}
                    learningOutcomeMappings={learningOutcomeMappings}
                    submittedPrograms={submittedPrograms}
                    onSetupSubmitChanged={onSetupSubmitChanged}
                    />
    } else if (currentStep === 4) {
        step = <SetupStep4
                    mode={MODE}
                    departmentId={departmentId}
                    departments={departments}
                    activePrograms={activePrograms}
                    programLearningOutcomes={programLearningOutcomes[departmentId].learningOutcomes}
                    learningOutcomeMappings={learningOutcomeMappings}
                    submittedPrograms={submittedPrograms}
                    submittedUsers={submittedUsers}
                    />
    } else if (currentStep === 5) {
        step = <SetupStep5
                    />
    }
    return (
        <React.Fragment>
            <form>
                {step}
                <div className="py-3 mx-auto text-center">
                    {errorFound &&
                        <div style={{color: "red"}}>
                            {errorMessage}
                        </div>
                    }
                </div>
                { currentStep < 5 &&
                <div className="form-button-group text-center">
                
                    <span className="col-md-6 px-1">
                        <Button
                            onClick={handleBack}
                            disabled={currentStep <= 1}
                            >
                            Back
                        </Button>
                    </span>
                    <span className="col-md-6 px-1">
                        <Button
                            onClick={handleNext}
                            disabled={!validateStepOne()}
                            >
                            {currentStep < (STEPS.length) ? 'Next' : 'Save'}
                        </Button>
                    </span>
                </div>
                }
            </form>
        </React.Fragment>
    )
}